/**
 * Pool Reports Store
 *
 * Manages state for monthly and annual pool reports
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { PoolReportsService } from '@/services/poolReportsService';
import type { PoolReportsState, ReportType, ReportList } from '@/types/poolReports.types';

export interface SubTitle {
  value: string;
  backLink?: string;
  queryParams?: Record<string, string>;
}

interface PoolReportsStore extends PoolReportsState {
  subTitle: SubTitle | null;
  // Actions
  setReportType: (reportType: ReportType) => void;
  setSelectedPoolId: (poolId: number | null) => void;
  setSelectedReport: (report: ReportList | null) => void;
  setSubTitle: (subTitle: SubTitle | null) => void;
  fetchPools: () => Promise<void>;
  fetchReportList: (poolId: number) => Promise<void>;
  fetchDownloadables: (fileId: number) => Promise<void>;
  downloadFile: (fileId: number, type: 'xml' | 'excel') => Promise<void>;
  clearError: () => void;
}

export const usePoolReportsStore = create<PoolReportsStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      reportType: 'yearly',
      reportList: [],
      selectedReport: null,
      selectedPoolId: null as number | null,
      pools: [],
      subTitle: null,
      loading: false,
      error: null,
      downloadable: null,

      // Actions
      setReportType: (reportType) => {
        set({
          reportType,
          reportList: [],
          selectedReport: null,
        });
      },

      setSelectedPoolId: (poolId) => {
        set({ selectedPoolId: poolId });
      },

      setSelectedReport: (report) => {
        set({ selectedReport: report });
      },

      setSubTitle: (subTitle) => {
        set({ subTitle });
      },

      fetchPools: async () => {
        set({ loading: true, error: null });

        try {
          const pools = await PoolReportsService.getPools();
          set({ pools, loading: false });

          // Auto-select first pool if no pool is selected
          const { selectedPoolId } = get();
          if (pools.length > 0 && !selectedPoolId) {
            set({ selectedPoolId: pools[0].PoolId });
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch pools';
          set({ error: errorMessage, loading: false });
        }
      },

      fetchReportList: async (poolId) => {
        const { reportType } = get();
        set({ loading: true, error: null, selectedReport: null });

        try {
          const reportList = await PoolReportsService.getReportList(reportType, poolId);
          set({
            reportList,
            loading: false,
            selectedPoolId: poolId,
          });

          // Auto-select first report
          if (reportList.length > 0) {
            set({ selectedReport: reportList[0] });
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to fetch report list';
          set({ error: errorMessage, loading: false });
        }
      },

      fetchDownloadables: async (fileId) => {
        set({ loading: true, error: null });

        try {
          const downloadable = await PoolReportsService.getDownloadables(fileId);
          set({ downloadable, loading: false });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to fetch downloadable files';
          set({ error: errorMessage, loading: false });
        }
      },

      downloadFile: async (fileId, type) => {
        set({ loading: true, error: null });

        try {
          const { blob, fileName } = await PoolReportsService.downloadFile(fileId, type);
          PoolReportsService.triggerDownload(blob, fileName);
          set({ loading: false });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to download file';
          set({ error: errorMessage, loading: false });
        }
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'pool-reports-store',
    }
  )
);
